import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, IconButton, InputAdornment, Box
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

export default function EditUserDialogSimple({ open, onClose, userProfile, onSave, mode = "edit", disabled }) {
  const { t } = useTranslation();
  const isCreate = mode === "create";

  const [form, setForm] = useState({
    username: "",
    first_name: "",
    last_name: "",
    email: "",
    dataview_pin: "",
    password: "",
  });
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (isCreate || !userProfile) {
      setForm({ username: "", first_name: "", last_name: "", email: "", dataview_pin: "", password: "" });
      return;
    }
    setForm({
      username: userProfile?.user?.username || "",
      first_name: userProfile?.user?.first_name || "",
      last_name: userProfile?.user?.last_name || "",
      email: userProfile?.user?.email || "",
      dataview_pin: userProfile?.dataview_pin || "",
      password: "",
    });
  }, [userProfile, isCreate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const canSave =
    !disabled &&
    !saving &&
    (isCreate ? !!form.username.trim() && !!form.password : true);

  const handleSave = async () => {
    if (isCreate && (!form.username.trim() || !form.password)) return;

    setSaving(true);
    try {
      const payload = {
        username: form.username,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        dataview_pin: form.dataview_pin,
        ...(form.password ? { password: form.password } : {}),
      };

      await onSave(userProfile?.id, payload, mode);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isCreate ? t("editUserDialog.titleCreate", "Create User") : t("editUserDialog.titleEdit", "Edit User")}
      </DialogTitle>

      <DialogContent dividers>
        <TextField
          fullWidth
          required={isCreate}
          label={t("editUserDialog.fields.username", "Username")}
          name="username"
          value={form.username}
          onChange={handleChange}
          margin="normal"
          disabled={disabled || saving || (!isCreate)} // don't allow changing username on edit (optional)
        />

        <TextField
          fullWidth
          required={isCreate}
          label={isCreate ? t("editUserDialog.fields.password", "Password") : t("editUserDialog.fields.newPassword", "New password (optional)")}
          name="password"
          type={showPw ? "text" : "password"}
          value={form.password}
          onChange={handleChange}
          margin="normal"
          disabled={disabled || saving}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPw((s) => !s)} edge="end">
                  {showPw ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          helperText={
            isCreate
              ? t("editUserDialog.validation.passwordRequired", "Password is required")
              : t("editUserDialog.help.passwordLeaveBlank", "Leave blank to keep current password")
          }
        />

        <Box display="flex" gap={2}>
          <TextField
            fullWidth
            label={t("editUserDialog.fields.firstName", "First name")}
            name="first_name"
            value={form.first_name}
            onChange={handleChange}
            margin="normal"
            disabled={disabled || saving}
          />
          <TextField
            fullWidth
            label={t("editUserDialog.fields.lastName", "Last name")}
            name="last_name"
            value={form.last_name}
            onChange={handleChange}
            margin="normal"
            disabled={disabled || saving}
          />
        </Box>

        <TextField
          fullWidth
          label={t("editUserDialog.fields.email", "Email")}
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
          margin="normal"
          disabled={disabled || saving}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          {t("editUserDialog.actions.cancel", "Cancel")}
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          {saving
            ? (isCreate ? t("editUserDialog.actions.creating", "Creating...") : t("editUserDialog.actions.saving", "Saving..."))
            : (isCreate ? t("editUserDialog.actions.create", "Create") : t("editUserDialog.actions.save", "Save"))}
        </Button>
      </DialogActions>
    </Dialog>
  );
}